import { Suspense, useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import EnvironmentBadge from '../EnvironmentBadge';
import OfflineBanner from '../shared/OfflineBanner';
import RoleOverrideBanner from '../shared/RoleOverrideBanner';
import ErrorBoundary from '../shared/ErrorBoundary';
import PageLoading from '../shared/PageLoading';
import UniversalSearch from '../shared/UniversalSearch';
import FeedbackFAB from '../shared/FeedbackFAB';
import TeamCelebration from '../shared/TeamCelebration';
import { usePWAMode } from '../../hooks/usePWAMode';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { capture } from '../../analytics/posthog';

function topLevelModule(pathname) {
  const segment = pathname.split('/').filter(Boolean)[0];
  return segment || 'home';
}

export default function AuthenticatedLayout() {
  const location = useLocation();
  const isPWA = usePWAMode();
  const { isOffline } = useOnlineStatus();
  const pwaOffline = isPWA && isOffline;
  const [searchOpen, setSearchOpen] = useState(false);
  const lastModuleRef = useRef(null);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  useEffect(() => {
    const mod = topLevelModule(location.pathname);
    if (mod !== lastModuleRef.current) {
      lastModuleRef.current = mod;
      capture('module_opened', { module: mod });
    }
  }, [location.pathname]);

  // Global Cmd+K / Ctrl+K shortcut (disabled in PWA offline mode)
  useEffect(() => {
    if (pwaOffline) return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pwaOffline]);

  return (
    <>
      <EnvironmentBadge />
      <OfflineBanner />
      <RoleOverrideBanner />
      <div className="flex min-h-screen" style={{ backgroundColor: 'var(--ae-bg)' }}>
        <Sidebar onOpenSearch={pwaOffline ? null : openSearch} />
        {/* sm:pb-24 reserves scroll room under the FeedbackFAB (fixed bottom-6 right-6,
            shown at sm and up) so a control at the end of a page — e.g. the Remove
            button on the last row of a deal's wells table — is never stuck beneath it.
            The container's own sm:py-6 is smaller than the FAB's footprint. */}
        <main className="flex-1 min-w-0 overflow-x-hidden pt-[calc(52px+env(safe-area-inset-top))] sm:pt-0 sm:pb-24">
          {/* One content width for every screen. There used to be a `fullBleed`
              exception here, carried solely because DealsList managed its own
              width and padding; it now uses <PageHeader> like everything else. */}
          <div className="max-w-[1200px] mx-auto px-4 py-4 sm:px-6 sm:py-6">
            <ErrorBoundary key={location.pathname}>
              <Suspense fallback={<PageLoading />}>
                <Outlet />
              </Suspense>
            </ErrorBoundary>
          </div>
        </main>
      </div>
      {!pwaOffline && <UniversalSearch isOpen={searchOpen} onClose={closeSearch} />}
      {!pwaOffline && <FeedbackFAB />}
      {/* Mounted here, not on the invoice page, because the team celebration is
          delivered on app load wherever the person happens to land next. */}
      {!pwaOffline && <TeamCelebration />}
    </>
  );
}
